/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: vis-slot-editor
 *| (C) Copyright IBM Corp. 2019, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import { types, getEnv } from 'mobx-state-tree';
import SlotsModel from './SlotsModel';
import LocalFiltersModel from './LocalFiltersModel';
import _ from 'underscore';
import { getNumberOfFilterItems } from '../utils';
import { isAlive } from 'mobx-state-tree';
const SLOT_EDITOR_STATES = {
	DISPLAY: 'DISPLAY',
	EMPTY: {
		NO_VIS_CREATED: 'NO_VIS_CREATED',
		NOT_SUPPORT_SLOTS: 'NOT_SUPPORT_SLOTS',
		NO_VIS_SELECTED: 'NO_VIS_SELECTED',
		MUILTI_SELECTION: 'MUILTI_SELECTION',
		ERROR: 'ERROR'
	}
};

export default types
	.model('SlotEditorModel', {
		slotsModel: SlotsModel,
		localFiltersModel: LocalFiltersModel
	})
	.volatile(() => ({
		dashboardApi: null,
		canvas:null,
		selectedContents: [],
		slotEditorState: ''
	}))
	.views((self) => ({
		/**
		 * @return {array} slot apis
		 */
		getSlotInfo() {
			if(self.slotsModel && isAlive(self.slotsModel)){
				return self.slotsModel.getSlotInfo();
			} else {
				return [];
			}
		},
		/**
		 * @return {array} local filters
		 */
		getLocalFilters() {
			if(self.localFiltersModel && isAlive(self.localFiltersModel)){
				return self.localFiltersModel.getLocalFilters();
			} else {
				return [];
			}
		},
		/**
		 * @param {Object} slot - slot api
		 * @return {Object} - SlotDataItemHandler
		 */
		getSlotDataItemHandler(slot, indexInSlot) {
			if (self.slotsModel) {
				return self.slotsModel.getSlotDataItemHandler(slot, indexInSlot);
			} else {
				return null;
			}
		},
		/**
		 * @return {Object} - FilterActionHandler 
		 */
		getFilterActionHandler() {
			if (self.localFiltersModel) {
				return self.localFiltersModel.getFilterActionHandler();
			} else {
				return null;
			}
		},

		get SLOT_EDITOR_STATES() {
			return SLOT_EDITOR_STATES;
		}
	}))
	.actions((self) => {
		const listeners = [];
		const _getLocalFiltersInfo = function(visualization) {
			const localFilters = visualization.getLocalFilters();
			const localFiltersInfo = localFilters.getFilterList();
			if(localFiltersInfo && localFiltersInfo.length) {
				// These are what we will display in the `local filters` in the slot editor
				const mappedItemIds = {};
				visualization.getSlots().getMappedSlotList().forEach((mappedSlot) => {
					mappedSlot.getDataItemList().forEach((dataItem) => {
						mappedItemIds[dataItem.getColumnId()] = true;
					});
				});

				const localFiltersDesc = localFilters.getFiltersDesc();
				return localFiltersInfo
					.filter((localFilterInfo) => {
						let shouldShowInLocalFilter = false;
						if(localFilterInfo.columnId){
							shouldShowInLocalFilter = !(mappedItemIds[localFilterInfo.columnId] && !localFilterInfo.readOnly);
						}
						return shouldShowInLocalFilter;
					})
					.map((localFilterInfo) => {
						const foundLocalFilterDesc = !(localFilterInfo.isMissing) && localFiltersDesc && localFiltersDesc.find((localFilterDesc) => localFilterDesc.dataId === localFilterInfo.id);
						const dataSource = visualization.getDataSource();
						// Make sure the metadata is loaded before accessing it to get column info
						const metadataIsLoaded = dataSource && dataSource.getState() === dataSource.STATE.READY;
						return {
							id: localFilterInfo.id,
							columnId: localFilterInfo.columnId,
							metadataColumn: localFilterInfo.isMissing || !metadataIsLoaded ? null : dataSource.getMetadataColumn(localFilterInfo.columnId),
							name: localFilterInfo.isMissing ? null : foundLocalFilterDesc && foundLocalFilterDesc.title,
							filterString: foundLocalFilterDesc && foundLocalFilterDesc.description,
							numberOfFilterItems: getNumberOfFilterItems(localFilterInfo),
							isMissing: localFilterInfo.isMissing
						};
					});
			}
			return null;
		};

		/**
		 * @param {*} layerId
		 * @returns true/false to hide/show layers
		 */
		const _isHideLayer = function (visualization, layerId) {
			const visDefinition = visualization.getDefinition();
			const datasets = visDefinition.getDatasetList() || [];
			const layer = datasets.find((dataset) => {
				return dataset.id === layerId;
			}) || {};
			return !layer.caption;
		};

		/**
		* returns an array of objects that describe slots, the views and layers they're associated with,
		* TODO: In future, this function can be extended to include items that are 'context' (dataItems in a view not mapped to a slot)
		* @param options Supports the 'includeUnmappedSlots: true' option which would return slotAPIs and layers for unmapped as well as mapped slots.
		* @returns an array of Slot/View information objects by view (or layer depending on how you think of it!)
		*/
		const _getSlotInfoForEachView = function(visualization) {
			const slots = visualization.getSlots().getSlotList();
			const slotDefinitions = visualization.getDefinition().getSlotList();
			const sharedSlots = slotDefinitions.filter(slotDefn => slotDefn.getDatasetIdList().length > 1);
			const slotAndViewInfoArray = [];
			if(sharedSlots.length > 0) {
				// single UI layer
				const defnLayerId = 'data';
				slotAndViewInfoArray.push(
					{
						slotAPIs: slots || [],
						layerId: defnLayerId,
						layerCaption: null,
						isHideLayer: _isHideLayer(visualization, defnLayerId)
					}
				);
			} else {
				const slotsGroupedByLayer = _.groupBy(slots, slot => slot.getDefinition().getDatasetIdList()[0]);
				//Ensure an array of slots arrays is returned in definition order.
				visualization.getDefinition().getDatasetList().forEach((dataset, index) => {
					const slotsByDataset = slotsGroupedByLayer[dataset.id];
					slotAndViewInfoArray.push(
						{
							slotAPIs: slotsByDataset || [],
							layerId: dataset.id,
							layerCaption: dataset.caption || dataset.id,
							isHideLayer: _isHideLayer(visualization, dataset.id),
							open: index === 0
						}
					);
				});
			}
			return slotAndViewInfoArray;
		};

		let _onSelectionChange = null;
		let _onModelChange = null;

		return {
			afterCreate() {
				const createModelChangeFn = (setupEvents) => {
					return () => {
						if(isAlive(self)) {
							self._updateModel(setupEvents);
						}
					};
				};

				// @todo: STOP using debounce
				// SHOULD use transaction feature instead
				// Event handling of selection and event handling for mapping change needs to be seprated.
				// Combining the two, results in unnecessary on/off upon mapping.
				// Added [setupEvents] to distinguish the two cases for now.

				//Need two debounce handlers as they deal with different events and interferes with each other.
				// The different usage of _updateModelOnChange handler
				// 1. handler for content selection change.
				_onSelectionChange = _.debounce(createModelChangeFn(true));
				// 2. hander for slots mapping change.
				_onModelChange = _.debounce(createModelChangeFn(false));

				self.dashboardApi = getEnv(self).dashboardApi;
				if (self.dashboardApi) {
					if (self.slotsModel) {
						self.slotsModel.setDashboardApi(self.dashboardApi);
					}
					if (self.localFiltersModel) {
						self.localFiltersModel.setDashboardApi(self.dashboardApi);
					}
					self.dashboardApi.getCanvasWhenReady().then((canvas) => {
						self.setCanvas(canvas);
						canvas.on('change:selections:select', _onSelectionChange);
						canvas.on('change:selections:deselect', _onSelectionChange);

						// listening on shaping changes
						listeners.push(
							self.dashboardApi.on('shapingmodel:changed', (payload) => {
								const needsRefresh = payload && !payload.noRefresh;
								if(needsRefresh) {
									_onModelChange();
								}
							})
						);

						// update the model to initial state
						_onModelChange();
					});
				}
			},
			beforeDestroy() {
				listeners.forEach(listener => listener.remove());
				if (self.canvas) {
					self.canvas.off('change:selections:select', _onSelectionChange);
					self.canvas.off('change:selections:deselect', _onSelectionChange);
				}
			},
			setCanvas(canvas) {
				self.canvas = canvas;
			},
			_updateModel(setupEvents) {
				self._setSlotEditorState();
				self._setSlotsAndLocalFilters(setupEvents);
			},
			_setSlotEditorState () {
				const selectedContents = self.canvas.getSelectedContentList();
				self.selectedContents = selectedContents;
				if(selectedContents.length === 0) { // no widget selected
					self.slotEditorState = SLOT_EDITOR_STATES.EMPTY.NO_VIS_SELECTED;
				} else if(selectedContents.length === 1) { // one widget selected
					const content = selectedContents[0];
					const contentType = content && content.getType();
					if (contentType === 'widget.live') { // live widget selected
						const visualization = content.getFeature('Visualization');
						if (visualization) {
							const visDefinition = visualization.getDefinition();
							// sets error state iff there exists vis definition error.
							if (visDefinition.getState().getError()) {
								self.slotEditorState = SLOT_EDITOR_STATES.EMPTY.ERROR;
							} else {
								const slots = visDefinition.getSlotList();
								if(slots && slots.length) {
									self.slotEditorState = SLOT_EDITOR_STATES.DISPLAY;
								} else {
									self.slotEditorState = SLOT_EDITOR_STATES.EMPTY.NOT_SUPPORT_SLOTS;
								}
							}
						} else {
							self.slotEditorState = SLOT_EDITOR_STATES.EMPTY.ERROR;
						}
					} else if(contentType === 'widget.placeholder' || contentType === 'widget.multipurpose') {
						self.slotEditorState = SLOT_EDITOR_STATES.EMPTY.NO_VIS_CREATED;
					} else if (contentType === 'group') {
						self.slotEditorState = SLOT_EDITOR_STATES.EMPTY.MUILTI_SELECTION;
					} else {// widget not support slots
						self.slotEditorState = SLOT_EDITOR_STATES.EMPTY.NOT_SUPPORT_SLOTS;
					}
				} else {// multiple widgets selected
					self.slotEditorState = SLOT_EDITOR_STATES.EMPTY.MUILTI_SELECTION;
				}
			},
			_setSlotsAndLocalFilters (setupEvents) {
				let content = null;
				let slotInfo = [];
				let localFilters = [];
				content = self.selectedContents && self.selectedContents[0];
				if (self.slotEditorState === SLOT_EDITOR_STATES.DISPLAY) {
					const visualization = content.getFeature('Visualization');
					if(visualization) {
						// If the visualization depends on a datasource, make sure the data source is ready to use before getting slots info  
						const dataSource = visualization.getDataSource();
						if (!dataSource || dataSource.getState() === dataSource.STATE.READY) {
							slotInfo = _getSlotInfoForEachView(visualization) || [];
							localFilters = _getLocalFiltersInfo(visualization) || [];
						}
					}
				}
				if (self.slotsModel && isAlive(self.slotsModel)) {
					if (setupEvents) {
						self.slotsModel.clear();
						self.slotsModel.setContent(content);
						self.slotsModel.setVisualization();
						self.slotsModel.setUpEvents(_onModelChange);
					}
					self.slotsModel.setSlotInfo(slotInfo);
				}
				if (self.localFiltersModel && isAlive(self.localFiltersModel)) {
					if (setupEvents) {
						self.localFiltersModel.clear();
						self.localFiltersModel.setContent(content);
						self.localFiltersModel.setUpEvents(_onModelChange);
					}
					self.localFiltersModel.setLocalFilters(localFilters);
				}
			}
		};
	});


// WEBPACK FOOTER //
// ./src/js/models/SlotEditorModel.js